import { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import jsPDF from 'jspdf';
import { Button } from '@/components/ui/button';
import { Printer, Download, Receipt } from 'lucide-react';
import { POSOrderWithItems } from '../../types/admin';
import { CustomerReceipt } from './CustomerReceipt';
import { KitchenTicket } from './KitchenTicket';
import { usePOSShopSettings } from '../../hooks/useShopSettings';

interface PrintManagerProps {
  order: POSOrderWithItems;
  type?: 'customer' | 'kitchen' | 'both';
}

export const PrintManager = ({ order, type = 'customer' }: PrintManagerProps) => {
  const { data: settings } = usePOSShopSettings();
  const customerReceiptRef = useRef<HTMLDivElement>(null);
  const kitchenTicketRef = useRef<HTMLDivElement>(null);

  // Browser Print - Customer Receipt
  const handlePrintCustomer = useReactToPrint({
    content: () => customerReceiptRef.current,
    documentTitle: `Reçu-${order.order_number}`,
    pageStyle: `
      @page {
        size: 80mm auto;
        margin: 0;
      }
      @media print {
        body {
          margin: 0;
          padding: 0;
        }
      }
    `,
  });

  // Browser Print - Kitchen Ticket
  const handlePrintKitchen = useReactToPrint({
    content: () => kitchenTicketRef.current,
    documentTitle: `Cuisine-${order.order_number}`,
    pageStyle: `
      @page {
        size: 80mm auto;
        margin: 0;
      }
      @media print {
        body {
          margin: 0;
          padding: 0;
        }
      }
    `,
  });

  // PDF Export - Customer Receipt
  const handleExportPDF = () => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [80, 200], // 80mm width, auto height
    });

    if (customerReceiptRef.current) {
      pdf.html(customerReceiptRef.current, {
        callback: (doc) => {
          doc.save(`recu-${order.order_number}.pdf`);
        },
        x: 0,
        y: 0,
        width: 80,
        windowWidth: 300,
      });
    }
  };

  // Print Both (Customer + Kitchen)
  const handlePrintBoth = () => {
    handlePrintCustomer();
    setTimeout(() => {
      handlePrintKitchen();
    }, 500);
  };

  return (
    <div className="space-y-4">
      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        <Button onClick={handlePrintCustomer} className="gap-2">
          <Printer className="w-4 h-4" />
          Imprimer Reçu
        </Button>

        <Button onClick={handlePrintKitchen} variant="outline" className="gap-2">
          <Receipt className="w-4 h-4" />
          Ticket Cuisine
        </Button>

        <Button onClick={handlePrintBoth} variant="outline" className="gap-2">
          <Printer className="w-4 h-4" />
          Imprimer Tout
        </Button>

        <Button onClick={handleExportPDF} variant="outline" className="gap-2">
          <Download className="w-4 h-4" />
          Export PDF
        </Button>
      </div>

      {/* Hidden Receipt Components for Printing */}
      <div className="hidden">
        <CustomerReceipt
          ref={customerReceiptRef}
          order={order}
          shopName={settings?.shop_name || 'Restaurant'}
          shopAddress={settings?.shop_address}
          shopPhone={settings?.shop_phone}
          shopTaxId={settings?.tax_id}
        />
      </div>

      <div className="hidden">
        <KitchenTicket
          ref={kitchenTicketRef}
          order={order}
          shopName={settings?.shop_name || 'Restaurant'}
        />
      </div>

      {/* Preview (Optional) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-semibold mb-2">Aperçu Reçu Client</h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden scale-75 origin-top-left">
            <CustomerReceipt
              order={order}
              shopName={settings?.shop_name || 'Restaurant'}
              shopAddress={settings?.shop_address}
              shopPhone={settings?.shop_phone}
              shopTaxId={settings?.tax_id}
            />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-2">Aperçu Ticket Cuisine</h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden scale-75 origin-top-left">
            <KitchenTicket
              order={order}
              shopName={settings?.shop_name || 'Restaurant'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
